// Phase 1 — Left collapsible sidebar. See docs/02-information-architecture.md §2.2.
//
// Presentation only: navigates to existing routes, reproduces the legacy role
// gate (full nav shown only for roleId == 1, exactly like apparelMenu.tsx), and
// derives the active item from useLocation(). No routes or guards are changed.
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import AppImages from "../Globals/AppImages";
import { getUser } from "../Utils/localStorage";
import { NAV_GROUPS, NavGroup, NavItem } from "./navigation";
import { cn } from "lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "ui/tooltip";
import { AccountMenu } from "./account-menu";
import { ThemeToggle } from "./theme-toggle";

const OPEN_GROUPS_KEY = "mz-nav-open-groups";

function readOpenGroups(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(OPEN_GROUPS_KEY);
    if (stored) return JSON.parse(stored);
  } catch (ex) {
    /* ignore */
  }
  return Object.fromEntries(NAV_GROUPS.map((g) => [g.id, true]));
}

type AppSidebarProps = {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Rendered inside the mobile Sheet — never a rail, closes on navigate. */
  mobile?: boolean;
  onNavigate?: () => void;
};

export function AppSidebar({ collapsed = false, onToggleCollapse, mobile = false, onNavigate }: AppSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user: any = getUser();
  const isAdmin = user?.roleId == 1; // legacy gate: full menu only for admin role

  const rail = collapsed && !mobile;

  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(readOpenGroups);
  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next));
      } catch (ex) {
        /* ignore */
      }
      return next;
    });

  const isActive = (to: string) => location.pathname === to;
  const go = (to: string) => {
    navigate(to);
    onNavigate?.();
  };

  const renderItem = (item: NavItem) => {
    const active = isActive(item.to);
    const Icon = item.icon;
    const button = (
      <button
        type="button"
        onClick={() => go(item.to)}
        aria-current={active ? "page" : undefined}
        className={cn(
          "group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium outline-none transition-colors",
          "focus-visible:ring-2 focus-visible:ring-ring",
          active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
          rail && "justify-center px-0"
        )}
      >
        {/* active accent bar */}
        {active && !rail && (
          <span className="absolute left-0 top-1.5 h-[calc(100%-0.75rem)] w-1 rounded-r bg-primary" />
        )}
        <Icon className="h-[18px] w-[18px] shrink-0" />
        {!rail && <span className="truncate">{item.label}</span>}
      </button>
    );

    if (rail) {
      return (
        <Tooltip key={item.to} delayDuration={0}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      );
    }
    return <React.Fragment key={item.to}>{button}</React.Fragment>;
  };

  const renderGroup = (group: NavGroup) => {
    if (rail) {
      // Collapsed rail: flat icon list, group headers hidden.
      return (
        <div key={group.id} className="flex flex-col items-center gap-1 py-1">
          {group.items.map(renderItem)}
        </div>
      );
    }
    const open = openGroups[group.id] ?? true;
    const GroupIcon = group.icon;
    return (
      <div key={group.id} className="mb-1">
        <button
          type="button"
          onClick={() => toggleGroup(group.id)}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <GroupIcon className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left">{group.label}</span>
          <ChevronDown className={cn("h-4 w-4 transition-transform", !open && "-rotate-90")} />
        </button>
        {open && <div className="mt-0.5 flex flex-col gap-0.5 pl-1">{group.items.map(renderItem)}</div>}
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          "mz flex h-full flex-col border-r bg-card",
          rail ? "w-16" : "w-60"
        )}
      >
        {/* Brand + collapse toggle */}
        <div className={cn("flex h-16 items-center border-b px-3", rail ? "justify-center" : "justify-between")}>
          <button
            type="button"
            onClick={() => go("/inventoryManagement")}
            aria-label="Mukizh home"
            className="flex items-center outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img src={AppImages?.logo} alt="Mukizh" className={cn("object-contain", rail ? "h-8" : "h-9")} />
          </button>
          {!mobile && (
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-label={rail ? "Expand sidebar" : "Collapse sidebar"}
              className={cn(
                "rounded-md p-1.5 text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
                rail && "absolute"
              )}
            >
              {rail ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {isAdmin ? (
            NAV_GROUPS.map(renderGroup)
          ) : (
            // Non-admin: legacy menu showed no navigation links. Reproduced.
            <p className={cn("px-3 py-2 text-xs text-muted-foreground", rail && "hidden")}>
              No modules available for your role.
            </p>
          )}
        </nav>

        {/* Footer: account + theme */}
        <div className={cn("border-t p-2", rail ? "flex flex-col items-center gap-1" : "flex items-center gap-1")}>
          <div className={cn(rail ? "" : "min-w-0 flex-1")}>
            <AccountMenu showName={!rail} align={rail ? "end" : "start"} />
          </div>
          <ThemeToggle />
        </div>
      </aside>
    </TooltipProvider>
  );
}
